/**
 * /dashboard/payroll/$id — Payroll run detail (G5.5).
 *
 * Data: GET /api/v1/payroll/runs/{id} returns the full payrollRunDTO
 * with embedded snapshot rows. The RunDetail component owns the
 * presentation; this route is the loading-state shell + role-gate.
 *
 * Backend visibility is HR-admin only for every /payroll/* endpoint
 * (no employee-self read in v1 — payslips are a separate G5.5 slice).
 * The PayrollRoleGate matches; backend remains the boundary.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/payroll/[id]/page.tsx with:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { usePayrollRun } from "@/hooks/use-payroll";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { RunDetail } from "@/components/payroll/run-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatPeriod } from "@/types/payroll";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/payroll/$id")({
  component: PayrollRunDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-32 w-full" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
      <Skeleton className="h-96 w-full" />
    </div>
  );
}

function PayrollRunDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = usePayrollRun(numId, validId);

  return (
    <PayrollModuleShell
      title="Payroll run"
      description={
        q.data
          ? formatPeriod(q.data.period_year, q.data.period_month)
          : undefined
      }
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/payroll" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid run id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid payroll run id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this run</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/payroll" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to payroll
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && <RunDetail initialRun={q.data} />}
    </PayrollModuleShell>
  );
}
